export const DEVICE_ID_STORAGE_KEY = 'qr-card-device-id';
export const DEVICE_MAC_STORAGE_KEY = 'qr-card-mac-address';

export interface DeviceMetadata {
  deviceId: string;
  macAddress?: string;
  deviceName?: string;
  platform?: string;
  browser?: string;
  language?: string;
  screenResolution?: string;
  fingerprint?: string;
  appVersion?: string;
  osVersion?: string;
  browserVersion?: string;
  extra?: string;
  timezone?: string;
  timezoneOffsetMinutes?: number;
  continent?: string;
  country?: string;
  city?: string;
  scannedAtLocal?: string;
  clientIp?: string;
}

const safeBtoa = (input: string): string | undefined => {
  try {
    if (!input || typeof window === 'undefined' || typeof window.btoa !== 'function') return undefined;
    return window.btoa(encodeURIComponent(input));
  } catch {
    return undefined;
  }
};

export const ensureDeviceId = (): string => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  try {
    const existing = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (existing) {
      return existing;
    }

    const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

    window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
  }
};

export const setDeviceMacAddress = (macAddress: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (macAddress) {
      window.localStorage.setItem(DEVICE_MAC_STORAGE_KEY, macAddress);
    } else {
      window.localStorage.removeItem(DEVICE_MAC_STORAGE_KEY);
    }
  } catch { 
  }
};

export const getStoredMacAddress = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return window.localStorage.getItem(DEVICE_MAC_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

const deriveDeviceName = (navigatorObj: Navigator): string | undefined => {
  const navAny = navigatorObj as any;
  if (navAny?.userAgentData?.platform) {
    return navAny.userAgentData.platform;
  }
  if (navigatorObj.platform) {
    return navigatorObj.platform;
  }
  if (navigatorObj.vendor) {
    return navigatorObj.vendor;
  }
  return undefined;
};

const deriveBrowserVersion = (navigatorObj: Navigator): string | undefined => {
  if (!navigatorObj.userAgent) return undefined;
  return navigatorObj.userAgent;
};

const deriveOsVersion = (navigatorObj: Navigator): string | undefined => {
  if (!navigatorObj.userAgent) return undefined;
  return navigatorObj.userAgent;
};

interface GeoLookupResult {
  ip?: string;
  continent?: string;
  continent_name?: string;
  continent_code?: string;
  country?: string;
  country_name?: string;
  city?: string;
  timezone?: string;
  utc_offset?: string;
}
const CONTINENT_NAMES_BY_CODE: Record<string, string> = {
  AF: 'Africa',
  AN: 'Antarctica',
  AS: 'Asia',
  EU: 'Europe',
  NA: 'North America',
  OC: 'Oceania',
  SA: 'South America',
};


let geoLookupPromise: Promise<GeoLookupResult | null> | null = null;
let geoLookupCache: GeoLookupResult | null = null;

const formatLocalDateTimeWithOffset = (date: Date): string => {
  const pad = (value: number, length: number = 2) => value.toString().padStart(length, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const milliseconds = pad(date.getMilliseconds(), 3);

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = pad(Math.floor(absOffset / 60));
  const offsetMins = pad(absOffset % 60);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${sign}${offsetHours}:${offsetMins}`;
};

const parseUtcOffsetToMinutes = (offset?: string | null): number | undefined => {
  if (!offset || offset.length < 3) return undefined;
  const normalized = offset.trim();
  const sign = normalized[0] === '-' ? -1 : 1;
  const digits = normalized.replace(/[^0-9]/g, '');
  if (digits.length < 2) return undefined;
  const hours = parseInt(digits.slice(0, 2), 10);
  const minutes = digits.length >= 4 ? parseInt(digits.slice(2, 4), 10) : 0;
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return undefined;
  return sign * (hours * 60 + minutes);
};

const resolveGeoLookup = async (): Promise<GeoLookupResult | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (geoLookupCache) {
    return geoLookupCache;
  }

  if (!geoLookupPromise) {
    geoLookupPromise = fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          return null;
        }
        return (await response.json()) as GeoLookupResult;
      })
      .catch(() => null)
      .finally(() => {
        geoLookupPromise = null;
      });
  }

  geoLookupCache = await geoLookupPromise;
  return geoLookupCache;
};

export const collectDeviceMetadata = async (): Promise<DeviceMetadata> => {
  const metadata: DeviceMetadata = {
    deviceId: ensureDeviceId(),
  };

  if (typeof window === 'undefined') {
    return metadata;
  }

  const navigatorObj = window.navigator;
  const now = new Date();

  metadata.macAddress = getStoredMacAddress();
  metadata.deviceName = deriveDeviceName(navigatorObj);
  metadata.platform = (navigatorObj as any)?.userAgentData?.platform ?? navigatorObj.platform ?? undefined;
  metadata.browser = navigatorObj.userAgent || undefined;
  metadata.language = navigatorObj.language || (navigatorObj.languages?.[0] ?? undefined);
  metadata.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? undefined;
  metadata.timezoneOffsetMinutes = -now.getTimezoneOffset();
  metadata.screenResolution = window.screen ? `${window.screen.width}x${window.screen.height}` : undefined;
  metadata.appVersion = navigatorObj.appVersion || undefined;
  metadata.osVersion = deriveOsVersion(navigatorObj);
  metadata.browserVersion = deriveBrowserVersion(navigatorObj);
  metadata.extra = window.location?.search || undefined;
  metadata.scannedAtLocal = formatLocalDateTimeWithOffset(now);

  try {
    const geo = await resolveGeoLookup();
    if (geo) {
      metadata.clientIp = geo.ip ?? metadata.clientIp;
      metadata.continent = geo.continent_name
        ?? geo.continent
        ?? (geo.continent_code ? CONTINENT_NAMES_BY_CODE[geo.continent_code] : undefined)
        ?? metadata.continent;
      metadata.country = geo.country_name ?? geo.country ?? metadata.country;
      metadata.city = geo.city ?? metadata.city;
      metadata.timezone = geo.timezone ?? metadata.timezone;
      const geoOffset = parseUtcOffsetToMinutes(geo.utc_offset);
      if (typeof geoOffset === 'number') {
        metadata.timezoneOffsetMinutes = geoOffset;
      }
    }
  } catch {
    // ignore geo lookup failures
  }

  const fingerprintSeed = [
    metadata.deviceId,
    metadata.macAddress,
    metadata.platform,
    metadata.language,
    metadata.screenResolution,
    metadata.timezone,
    metadata.timezoneOffsetMinutes?.toString(),
    metadata.continent,
    metadata.country,
    metadata.city,
  ]
    .filter(Boolean)
    .join('|');

  metadata.fingerprint = safeBtoa(fingerprintSeed);

  return metadata;
};


